import ImportAPI from '../../api/import';

const initialState = {
  records: [],
  blobId: null,
  recordsMeta: {
    total: 0,
    success: 0,
    failed: 0,
    errors: [],
    conversations: {
      success: 0,
      failed: 0,
    },
    contactsData: [],
    labels: {
      success: 0,
      failed: 0,
    },
    existing: 0,
    invalid: 0,
    duplicate: 0,
    existingContacts: [],
    invalidNumbers: [],
    duplicateNumbers: [],
  },
  imports: [],
  uiFlags: {
    isImporting: false,
    isUploading: false,
    isFetchingImports: false,
    hasError: false,
    errorMessage: '',
  },
  batchProgress: {
    currentBatch: 0,
    totalBatches: 0,
    totalRecords: 0,
    processedRecords: 0,
  },
};

export const getters = {
  getRecords: state => state.records,
  getRecordsMeta: state => state.recordsMeta,
  getImports: state => state.imports,
  getUIFlags: state => state.uiFlags,
  getBatchProgress: state => state.batchProgress,
};

export const actions = {
  async uploadFile({ commit }, { file, inboxId }) {
    commit('SET_UPLOADING', true);
    commit('SET_ERROR', { hasError: false, message: '' });

    try {
      const response = await ImportAPI.uploadFile(file, inboxId);
      commit('SET_RECORDS', response.data.data || response.data);
      commit('SET_BLOB_ID', response.data.blob_id);
      commit('SET_UPLOADING', false);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      commit('SET_UPLOADING', false);
      throw error;
    }
  },

  // ação de importação normal removida; utilizamos apenas importação em lotes

  async importContactsBatch({ commit, state }, { inboxId }) {
    const batchSize = 100;
    const totalRecords = state.records.length;
    const totalBatches = Math.ceil(totalRecords / batchSize);

    commit('SET_IMPORTING', true);
    commit('SET_ERROR', { hasError: false, message: '' });
    commit('SET_BATCH_PROGRESS', {
      currentBatch: 0,
      totalBatches,
      totalRecords,
      processedRecords: 0,
    });

    // Acumular resultados de todos os lotes
    let accumulatedResults = {
      total: totalRecords,
      success: 0,
      failed: 0,
      errors: [],
      conversations: { success: 0, failed: 0 },
      contactsData: [],
      labels: { success: 0, failed: 0 },
      existing: 0,
      invalid: 0,
      duplicate: 0,
      existingContacts: [],
      invalidNumbers: [],
      duplicateNumbers: [],
    };

    try {
      for (let batchNumber = 1; batchNumber <= totalBatches; batchNumber += 1) {
        commit('UPDATE_BATCH_PROGRESS', { currentBatch: batchNumber });

        // Os lotes são intencionalmente sequenciais para manter o progresso determinístico.
        // eslint-disable-next-line no-await-in-loop
        const response = await ImportAPI.importContactsBatch(
          inboxId,
          state.blobId,
          batchNumber,
          batchSize
        );
        const batchResult = response.data;

        // Acumular resultados
        accumulatedResults.success += batchResult.success;
        accumulatedResults.failed += batchResult.failed;
        accumulatedResults.existing += batchResult.existing;
        accumulatedResults.invalid += batchResult.invalid || 0;
        accumulatedResults.duplicate += batchResult.duplicate || 0;
        accumulatedResults.errors = accumulatedResults.errors.concat(
          batchResult.errors || []
        );
        accumulatedResults.conversations.success +=
          batchResult.conversations?.success || 0;
        accumulatedResults.conversations.failed +=
          batchResult.conversations?.failed || 0;
        accumulatedResults.labels.success += batchResult.labels?.success || 0;
        accumulatedResults.labels.failed += batchResult.labels?.failed || 0;
        accumulatedResults.contactsData =
          accumulatedResults.contactsData.concat(
            batchResult.contactsData || []
          );
        accumulatedResults.existingContacts =
          accumulatedResults.existingContacts.concat(
            batchResult.existingContacts || batchResult.existingData || []
          );
        accumulatedResults.invalidNumbers =
          accumulatedResults.invalidNumbers.concat(
            batchResult.invalidNumbers || []
          );
        accumulatedResults.duplicateNumbers =
          accumulatedResults.duplicateNumbers.concat(
            batchResult.duplicateNumbers || []
          );

        const realProcessedRecords =
          accumulatedResults.success +
          accumulatedResults.existing +
          accumulatedResults.failed +
          accumulatedResults.invalid +
          accumulatedResults.duplicate;
        commit('UPDATE_BATCH_PROGRESS', {
          processedRecords: realProcessedRecords,
        });

        // Atualizar meta com resultados acumulados
        commit('SET_RECORDS_META', accumulatedResults);

        // Sem delay entre lotes para agilizar o processamento
      }

      commit('SET_IMPORTING', false);
      return accumulatedResults;
    } catch (error) {
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      commit('SET_IMPORTING', false);
      throw error;
    }
  },

  async deleteContacts({ commit }, { fileName }) {
    commit('SET_IMPORTING', true);
    commit('SET_ERROR', { hasError: false, message: '' });

    try {
      const response = await ImportAPI.deleteContacts(fileName);
      commit('SET_IMPORTING', false);
      return response.data;
    } catch (error) {
      commit('SET_IMPORTING', false);
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      throw error;
    }
  },

  async deleteContactsBatch({ commit }, { fileName, totalContacts }) {
    const batchSize = 25;
    const totalBatches = Math.ceil(totalContacts / batchSize);

    commit('SET_IMPORTING', true);
    commit('SET_ERROR', { hasError: false, message: '' });
    commit('SET_BATCH_PROGRESS', {
      currentBatch: 0,
      totalBatches,
      totalRecords: totalContacts,
      processedRecords: 0,
    });

    // Acumular resultados de todos os lotes
    let accumulatedResults = {
      total: 0,
      success: 0,
      failed: 0,
      errors: [],
    };

    try {
      for (let batchNumber = 1; batchNumber <= totalBatches; batchNumber += 1) {
        commit('UPDATE_BATCH_PROGRESS', { currentBatch: batchNumber });

        // eslint-disable-next-line no-await-in-loop
        const response = await ImportAPI.deleteContactsBatch(
          fileName,
          batchNumber,
          batchSize
        );
        const batchResult = response.data;

        // Acumular resultados
        accumulatedResults.total += batchResult.total;
        accumulatedResults.success += batchResult.success;
        accumulatedResults.failed += batchResult.failed;
        accumulatedResults.errors = accumulatedResults.errors.concat(
          batchResult.errors || []
        );

        // Atualizar progresso baseado nos contatos realmente processados
        const realProcessedRecords =
          accumulatedResults.success + accumulatedResults.failed;
        commit('UPDATE_BATCH_PROGRESS', {
          processedRecords: realProcessedRecords,
        });

        // Pausa de 2 segundos entre lotes (menor que importação), exceto no último lote
        if (batchNumber < totalBatches) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => {
            setTimeout(resolve, 2000);
          });
        }
      }

      commit('SET_IMPORTING', false);
      return accumulatedResults;
    } catch (error) {
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      commit('SET_IMPORTING', false);
      throw error;
    }
  },

  async deleteFile({ commit }, { fileName }) {
    commit('SET_IMPORTING', true);
    commit('SET_ERROR', { hasError: false, message: '' });

    try {
      const response = await ImportAPI.deleteFile(fileName);
      commit('SET_IMPORTING', false);
      return response.data;
    } catch (error) {
      commit('SET_IMPORTING', false);
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      throw error;
    }
  },

  async fetchImports({ commit }) {
    commit('SET_FETCHING_IMPORTS', true);
    commit('SET_ERROR', { hasError: false, message: '' });

    try {
      const response = await ImportAPI.fetchImports();
      commit('SET_IMPORTS', response.data.imports);
      commit('SET_FETCHING_IMPORTS', false);
      return response.data.imports;
    } catch (error) {
      commit('SET_ERROR', {
        hasError: true,
        message: error.response?.data?.error || error.message,
      });
      commit('SET_FETCHING_IMPORTS', false);
      throw error;
    }
  },

  clearData({ commit }) {
    commit('CLEAR_DATA');
  },
};

export const mutations = {
  SET_RECORDS(state, records) {
    state.records = records;
  },
  SET_BLOB_ID(state, blobId) {
    state.blobId = blobId;
  },
  SET_RECORDS_META(state, meta) {
    state.recordsMeta = meta;
  },
  SET_IMPORTS(state, imports) {
    state.imports = imports;
  },
  SET_UPLOADING(state, isUploading) {
    state.uiFlags.isUploading = isUploading;
  },
  SET_IMPORTING(state, isImporting) {
    state.uiFlags.isImporting = isImporting;
  },
  SET_FETCHING_IMPORTS(state, isFetchingImports) {
    state.uiFlags.isFetchingImports = isFetchingImports;
  },
  SET_ERROR(state, { hasError, message }) {
    state.uiFlags.hasError = hasError;
    state.uiFlags.errorMessage = message;
  },
  SET_BATCH_PROGRESS(state, progress) {
    state.batchProgress = { ...state.batchProgress, ...progress };
  },
  UPDATE_BATCH_PROGRESS(state, progress) {
    state.batchProgress = { ...state.batchProgress, ...progress };
  },
  CLEAR_DATA(state) {
    state.records = [];
    state.blobId = null;
    state.recordsMeta = {
      total: 0,
      success: 0,
      failed: 0,
      errors: [],
      conversations: {
        success: 0,
        failed: 0,
      },
      contactsData: [],
      labels: {
        success: 0,
        failed: 0,
      },
      existing: 0,
      invalid: 0,
      duplicate: 0,
      existingContacts: [],
      invalidNumbers: [],
      duplicateNumbers: [],
    };
    state.imports = [];
    state.uiFlags = {
      isImporting: false,
      isUploading: false,
      isFetchingImports: false,
      hasError: false,
      errorMessage: '',
    };
    state.batchProgress = {
      currentBatch: 0,
      totalBatches: 0,
      totalRecords: 0,
      processedRecords: 0,
    };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
